import { Box } from '@mui/material';
import React from 'react';
import { Creative } from 'types';
import placeholder from 'assets/images/placeholder_dashboard.svg';
import { isNoPreviewByOriginalFileName } from 'utils';

// Shared style properties for creative preview containers
const getPreviewContainerStyles = (spacing: (value: number) => string, noBorder?: boolean) => ({
  width: spacing(30),
  height: spacing(30),
  border: noBorder ? 'none' : '1px solid',
  borderColor: 'grey.200',
  borderRadius: spacing(3),
  backgroundColor: 'common.white',
});

type Props = {
  creative?: Omit<Creative, 'creativeType'> | null;
};

export const CreativePreview = ({ creative }: Props) => {
  if (creative && creative.url) {
    // Show creative preview with styling similar to Figma design
    const isInvalidPreview = creative.originalFileName && isNoPreviewByOriginalFileName(creative.originalFileName);

    if (isInvalidPreview) {
      // Show placeholder for invalid previews
      return (
        <Box
          component="img"
          sx={({ spacing }) => getPreviewContainerStyles(spacing, true)}
          alt="No creative preview available"
          src={placeholder}
        />
      );
    }

    // Check if it's a video creative based on file extension
    const isVideo = creative.originalFileName
      ? /\.(mp4|webm|ogg|avi|mov|wmv|flv|mkv)$/i.test(creative.originalFileName)
      : false;

    if (isVideo) {
      return (
        <Box
          sx={({ spacing }) => ({
            ...getPreviewContainerStyles(spacing),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
          })}
        >
          <Box
            component="video"
            sx={({ spacing }) => ({
              maxWidth: spacing(24),
              maxHeight: spacing(24),
              borderRadius: spacing(1),
              objectFit: 'cover',
            })}
            src={creative.url}
          />
        </Box>
      );
    }

    // Show image creative
    return (
      <Box
        sx={({ spacing }) => ({
          ...getPreviewContainerStyles(spacing),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        })}
      >
        <Box
          component="img"
          sx={({ spacing }) => ({
            maxWidth: spacing(24),
            maxHeight: spacing(24),
            borderRadius: spacing(1),
            objectFit: 'cover',
          })}
          alt="Creative preview"
          src={creative.url}
        />
      </Box>
    );
  }

  // Show placeholder when no creative
  return (
    <Box
      component="img"
      sx={({ spacing }) => ({
        width: spacing(30),
        height: spacing(30),
      })}
      alt="No creative preview available"
      src={placeholder}
    />
  );
};
